import {
  orderList, orderCount, orderStats, orderGet, orderEdit, orderDelete,
  productGet
} from 'api';
import { getErrorMessage } from 'utils/axios';

export const ORDER_ERROR = 'ORDER_ERROR';

export const ORDER_LIST_REQUEST = 'ORDER_LIST_REQUEST';
export const ORDER_LIST_SUCCESS = 'ORDER_LIST_SUCCESS';

export const ORDER_STATS_SUCCESS = 'ORDER_STATS_SUCCESS';

export const ORDER_ID_REQUEST = 'ORDER_ID_REQUEST';
export const ORDER_ID_SUCCESS = 'ORDER_ID_SUCCESS';

export const ORDER_UPDATE_REQUEST = 'ORDER_UPDATE_REQUEST';
export const ORDER_UPDATE_SUCCESS = 'ORDER_UPDATE_SUCCESS';

export const ORDER_DELETE_REQUEST = 'ORDER_DELETE_REQUEST';
export const ORDER_DELETE_SUCCESS = 'ORDER_DELETE_SUCCESS';

export const ORDER_STATUS_UPDATE_REQUEST = 'ORDER_STATUS_UPDATE_REQUEST';
export const ORDER_STATUS_UPDATE_SUCCESS = 'ORDER_STATUS_UPDATE_SUCCESS';

export const ORDER_CHANGE_PAGE = 'ORDER_CHANGE_PAGE';
export const ORDER_CHANGE_PAGE_SIZE = 'ORDER_CHANGE_PAGE_SIZE';
export const ORDER_SEARCH_QUERY = 'ORDER_SEARCH_QUERY';
export const ORDER_SET_FILTER = 'ORDER_SET_FILTER';
export const ORDER_CLEAR_FILTER = 'ORDER_CLEAR_FILTER';

const defaultHandlers = {
  onSuccess: () => {},
  onError: () => {}
};

// Load the current page of orders (+ total count) using the pagination/filter
// state held in the store. Server-side, so it no longer caps at 100 records.
export const loadOrders = (handlers = defaultHandlers) => async (dispatch, getStore) => {
  const { onSuccess, onError } = handlers;

  dispatch({ type: ORDER_LIST_REQUEST });

  const { orders } = getStore();
  const { page, pageSize, query, filter } = orders;
  const listFilter = {
    status: filter.status,
    amount: filter.amount,
    query
  };

  try {
    const [countRes, listRes] = await Promise.all([
      orderCount(listFilter),
      orderList(page, pageSize, listFilter)
    ]);
    dispatch({ type: ORDER_LIST_SUCCESS, data: listRes.data, count: countRes.data });
    onSuccess && onSuccess();
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch({ type: ORDER_ERROR, message });
    onError && onError(message);
  }
};

// Aggregated totals for the Overview dashboard. Fetches the most-ordered
// product so the dashboard can show its title without loading every order.
export const loadOrderStats = (handlers = defaultHandlers) => async (dispatch) => {
  const { onSuccess, onError } = handlers;

  try {
    const res = await orderStats();
    const stats = res.data || {};

    let mostOrderedProduct = null;
    if (stats.mostOrderedProductId) {
      try {
        const prodRes = await productGet(stats.mostOrderedProductId);
        mostOrderedProduct = prodRes.data;
      } catch (e) {
        // Product may have been deleted — leave it null, the UI shows "No Data".
      }
    }

    dispatch({ type: ORDER_STATS_SUCCESS, stats: { ...stats, mostOrderedProduct } });
    onSuccess && onSuccess();
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch({ type: ORDER_ERROR, message });
    onError && onError(message);
  }
};

export const changePage = (page, handlers = defaultHandlers) => async (dispatch) => {
  dispatch({ type: ORDER_CHANGE_PAGE, page });
  await dispatch(loadOrders(handlers));
};

export const changePageSize = (size, handlers = defaultHandlers) => async (dispatch) => {
  dispatch({ type: ORDER_CHANGE_PAGE_SIZE, pageSize: size });
  dispatch({ type: ORDER_CHANGE_PAGE, page: 0 });
  await dispatch(loadOrders(handlers));
};

export const searchOrders = (query, handlers = defaultHandlers) => async (dispatch) => {
  dispatch({ type: ORDER_SEARCH_QUERY, query });
  dispatch({ type: ORDER_CHANGE_PAGE, page: 0 });
  await dispatch(loadOrders(handlers));
};

export const applyOrderFilter = (filter = {}, handlers = defaultHandlers) => async (dispatch) => {
  dispatch({ type: ORDER_SET_FILTER, filter });
  dispatch({ type: ORDER_CHANGE_PAGE, page: 0 });
  await dispatch(loadOrders(handlers));
};

export const clearOrderFilter = (handlers = defaultHandlers) => async (dispatch) => {
  dispatch({ type: ORDER_CLEAR_FILTER });
  dispatch({ type: ORDER_CHANGE_PAGE, page: 0 });
  await dispatch(loadOrders(handlers));
};

export const getOrder = (id, handlers = defaultHandlers) => async (dispatch) => {
  const { onSuccess, onError } = handlers;
  dispatch({ type: ORDER_ID_REQUEST });

  try {
    // Always fetch fresh. Serving the cached list item (orders.data) made the
    // detail page revert to the old status right after saving an update.
    const res = await orderGet(id);
    dispatch({ type: ORDER_ID_SUCCESS, data: res.data });
    onSuccess && onSuccess();
  } catch (error) {
    const message = getErrorMessage(error)
    dispatch({ type: ORDER_ERROR, message });
    onError && onError(message);
  }
};

export const bulkMarkOrdersStatus = (ids, status, handlers = defaultHandlers) => async dispatch => {
  if (ids.length < 1) {
    return;
  }

  const { onSuccess, onError } = handlers;
  dispatch({ type: ORDER_STATUS_UPDATE_REQUEST });

  try {
    const res = await Promise.all(ids.map(id => orderEdit(id, { status })));
    dispatch({ type: ORDER_STATUS_UPDATE_SUCCESS, data: res.map(r => r.data) });
    onSuccess && onSuccess();
  } catch (error) {
    const message = getErrorMessage(error)
    dispatch({ type: ORDER_ERROR, message });
    onError && onError(message);
  }
};

export const updateOrder = (id, data, handlers = defaultHandlers) => async dispatch => {
  const { onSuccess, onError } = handlers;
  dispatch({ type: ORDER_UPDATE_REQUEST });

  try {
    const res = await orderEdit(id, { ...data });
    dispatch({ type: ORDER_UPDATE_SUCCESS, data: res.data });
    onSuccess && onSuccess();
  } catch (error) {
    const message = getErrorMessage(error)
    dispatch({ type: ORDER_ERROR, message });
    onError && onError(message);
  }
};

export const bulkDeleteOrders = (ids, handlers = defaultHandlers) => async dispatch => {
  if (ids.length < 1) {
    return;
  }

  const { onSuccess, onError } = handlers;
  dispatch({ type: ORDER_DELETE_REQUEST });

  try {
    const res = await Promise.all(ids.map(id => orderDelete(id)));
    dispatch({ type: ORDER_DELETE_SUCCESS, data: res.map(r => r.data) });
    onSuccess && onSuccess();
  } catch (error) {
    const message = getErrorMessage(error)
    dispatch({ type: ORDER_ERROR, message });
    onError && onError(message);
  }
};
